import React, { useContext, useMemo, useState, useCallback } from "react";
import KeyedDropdown from "../Dropdown/KeyedDropdown";
import classNames from "classnames";
import "./Roadmap.css";
import { useSpace } from "../../Contexts/SpaceContext";
import { spaceTasksSchema, spaceTicketsSchema } from "../../config/schema";
import { useToast } from "../Toast/ToastProvider";
import { usePost, useShortPost } from "../../Helpers/IOClient";
import { useIntl, FormattedMessage } from "react-intl";
import { handleError } from "../../Helpers/MiscHelper";
import { useTask, useTicket } from "../../Hooks/EntityHooks";

export const RoadmapTypeEnum = {
  Impact: 1,
  Effort: 2,
  Priority: 3
};

const options = [null, 1, 2, 3, 4, 5];

const RoadmapClassTypeEnum = {
  [RoadmapTypeEnum.Impact]: "impact-type",
  [RoadmapTypeEnum.Effort]: "effort-type",
  [RoadmapTypeEnum.Priority]: "priority-type"
};

export const RoadMapValueComponent = ({ filter, value, forcedType, item }) => {
  const type =
    useContext(RoadmapTypeContext) || forcedType || RoadmapTypeEnum.Impact;

  const resovledValue = useMemo(() => value || item, [item, value]);

  if (!resovledValue)
    return (
      <div
        className={classNames("d-flex gap-1 py-1 align-items-center", {
          "mt-1 mr-2": filter,
          "mr-3": !filter
        })}
      >
        <div
          className={classNames("roadmap-circle active visibility-none")}
        ></div>
      </div>
    );

  const classType = RoadmapClassTypeEnum[type];

  return (
    <div
      className={classNames("d-flex gap-1 py-1 align-items-center", classType, {
        "mt-1 mr-2": filter,
        "mr-3": !filter
      })}
    >
      <div
        className={classNames("roadmap-circle", resovledValue >= 1 && "active")}
      ></div>
      <div
        className={classNames("roadmap-circle", resovledValue >= 2 && "active")}
      ></div>
      <div
        className={classNames("roadmap-circle", resovledValue >= 3 && "active")}
      ></div>
      <div
        className={classNames("roadmap-circle", resovledValue >= 4 && "active")}
      ></div>
      <div
        className={classNames("roadmap-circle", resovledValue >= 5 && "active")}
      ></div>
    </div>
  );
};

const RoadmapTypeContext = React.createContext();

export const ImpactRoadmapDropdown = (props) => {
  return (
    <RoadmapTypeContext.Provider value={RoadmapTypeEnum.Impact}>
      <RoadmapDropdown {...props} />
    </RoadmapTypeContext.Provider>
  );
};

export const EffortRoadmapDropdown = (props) => {
  return (
    <RoadmapTypeContext.Provider value={RoadmapTypeEnum.Effort}>
      <RoadmapDropdown {...props} />
    </RoadmapTypeContext.Provider>
  );
};

export const PriorityRoadmapDropdown = (props) => {
  return (
    <RoadmapTypeContext.Provider value={RoadmapTypeEnum.Priority}>
      <RoadmapDropdown {...props} />
    </RoadmapTypeContext.Provider>
  );
};

const RoadmapDropdown = (props) => {
  const { ...rest } = props;

  return (
    <KeyedDropdown
      arrowClassnames={"text-primary ml-2 fs-14"}
      valueComponent={RoadMapValueComponent}
      className="fs-12 text-truncate"
      options={options}
      dropdownClassname="text-truncate"
      closeOnSelect
      {...rest}
    />
  );
};

export const TaskRoadmapUpdater = ({ id, rValue, type }) => {
  const space = useSpace();
  const url = useMemo(
    () =>
      `${spaceTasksSchema.getEndpoint(space.Id)}/${
        type === RoadmapTypeEnum.Impact
          ? "Impact"
          : type === RoadmapTypeEnum.Effort
          ? "Effort"
          : "Priority"
      }`,
    [space.Id, type]
  );
  // const [resolvedValue, setResolvedValue] = useState(rValue);

  const { Impact, Effort, Priority } = useTask(id);

  const createToast = useToast();
  const [post, { loading }] = useShortPost(url, spaceTasksSchema);

  const intl = useIntl();
  const onChange = useCallback(
    (newV) => {
      if (newV !== rValue) {
        const onSuccess = () => {
          const translated_Type = intl.formatMessage({ id: "TASK" });
          const translated_text =
            type === RoadmapTypeEnum.Impact
              ? "IMPACT_CHANGED_SUCCESS"
              : type === RoadmapTypeEnum.Effort
              ? "EFFORT_CHANGE_SUCCESS"
              : "PRIORITY_CHANGE_SUCCESS";

          createToast({
            pos: "tm",
            type: "success",
            title: translated_Type + ` ${id}`,
            description: <FormattedMessage id={translated_text} />
          });
          // setResolvedValue(newV);
        };

        const onError = ({ error }) => {
          handleError(createToast, error);
        };

        post({ TaskId: id, Value: newV }, onSuccess, onError);
      }
    },
    [createToast, id, intl, post, rValue, type]
  );

  const Component = useMemo(() => {
    switch (type) {
      case 1:
        return (
          <ImpactRoadmapDropdown
            key={Impact}
            disabled={loading}
            value={Impact}
            onChange={onChange}
            loading={loading}
            hideCancellation={true}
          />
        );

      case 2:
        return (
          <EffortRoadmapDropdown
            key={Effort}
            disabled={loading}
            value={Effort}
            onChange={onChange}
            loading={loading}
            hideCancellation={true}
          />
        );

      case 3:
        return (
          <PriorityRoadmapDropdown
            key={Priority}
            disabled={loading}
            value={Priority}
            onChange={onChange}
            loading={loading}
            hideCancellation={true}
          />
        );

      default:
        return React.Fragment;
    }
  }, [Effort, Impact, Priority, loading, onChange, type]);

  return (
    <div
      className="w-100 h-100"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {Component}
    </div>
  );
};

export const TicketRoadmapUpdater = ({ id, rValue, type }) => {
  const space = useSpace();
  const url = useMemo(
    () =>
      `${spaceTicketsSchema.getEndpoint(space.Id)}/${
        type === RoadmapTypeEnum.Impact
          ? "Impact"
          : type === RoadmapTypeEnum.Effort
          ? "Effort"
          : "Priority"
      }`,
    [space.Id, type]
  );
  // const [resolvedValue, setResolvedValue] = useState(rValue);

  const { Impact, Effort, Priority } = useTicket(id);

  const createToast = useToast();
  const [post, { loading }] = useShortPost(url, spaceTicketsSchema);

  const intl = useIntl();
  const onChange = useCallback(
    (newV) => {
      if (newV !== rValue) {
        const onSuccess = () => {
          const translated_Type = intl.formatMessage({ id: "TICKET" });
          const translated_text =
            type === RoadmapTypeEnum.Impact
              ? "IMPACT_CHANGED_SUCCESS"
              : type === RoadmapTypeEnum.Effort
              ? "EFFORT_CHANGE_SUCCESS"
              : "PRIORITY_CHANGE_SUCCESS";

          createToast({
            pos: "tm",
            type: "success",
            title: translated_Type + ` ${id}`,
            description: <FormattedMessage id={translated_text} />
          });
          // setResolvedValue(newV);
        };

        const onError = ({ error }) => {
          handleError(createToast, error);
        };

        post({ TicketId: id, Value: newV }, onSuccess, onError);
      }
    },
    [createToast, id, intl, post, rValue, type]
  );

  const Component = useMemo(() => {
    switch (type) {
      case 1:
        return (
          <ImpactRoadmapDropdown
            key={Impact}
            disabled={loading}
            value={Impact}
            onChange={onChange}
            loading={loading}
            hideCancellation={true}
          />
        );

      case 2:
        return (
          <EffortRoadmapDropdown
            key={Effort}
            disabled={loading}
            value={Effort}
            onChange={onChange}
            loading={loading}
            hideCancellation={true}
          />
        );

      case 3:
        return (
          <PriorityRoadmapDropdown
            key={Priority}
            disabled={loading}
            value={Priority}
            onChange={onChange}
            loading={loading}
            hideCancellation={true}
          />
        );

      default:
        return React.Fragment;
    }
  }, [Effort, Impact, Priority, loading, onChange, type]);

  return (
    <div
      className="w-100 h-100"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {Component}
    </div>
  );
};
